// =============================================
// MusicDashboard — the tile grid shown when the library has music (image 2)
// =============================================
// A grid of action tiles that drill into a shared MusicCollectionView (flat lists)
// or a group browse (by artist / language / album / mood). Also hosts the Wishlist
// and the "Update Song Info" editor. Playback is delegated up via onPlay.
// =============================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiClock, FiPlay, FiStar, FiHeart, FiGlobe, FiMic, FiFilm, FiSmile,
  FiList, FiTrash2, FiEdit2, FiPlusCircle, FiUploadCloud, FiHardDrive, FiArrowLeft, FiTrendingUp, FiImage,
} from 'react-icons/fi';
import { getAudioUrl, storeAudioBlob } from '../../lib/audioStore';
import { isValidCover } from './coverArt';
import {
  TrackView, TrackGroup, getRecentlyPlayed, getMostPlayed,
  groupByArtist, groupByAlbum, groupByLanguage, groupByMood,
  updateTrackInfo, getWishlist, addWishlistItem, setWishlistStatus, removeWishlistItem, WishlistItem,
} from './musicLibrary';
import MusicCollectionView from './MusicCollectionView';
import MusicPlaylists from './MusicPlaylists';

type ListFn = (userId: number, tracks: TrackView[]) => TrackView[];
type GroupFn = (tracks: TrackView[]) => TrackGroup[];

interface TileDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  tone: 'cool' | 'warm';
  kind: 'list' | 'group' | 'wishlist' | 'playlists' | 'playAll' | 'upload';
  listFn?: ListFn;
  groupFn?: GroupFn;
  subtitle?: string;
  editInfo?: boolean;
}

const TILES: TileDef[] = [
  { key: 'all',        label: 'All Songs',                 icon: <FiHardDrive />, tone: 'cool', kind: 'list',  listFn: (_u, t) => t },
  { key: 'playall',    label: 'Play All',                  icon: <FiPlay />,      tone: 'warm', kind: 'playAll' },
  { key: 'recent',     label: 'Recently Played',           icon: <FiClock />,     tone: 'cool', kind: 'list',  listFn: (u) => getRecentlyPlayed(u) },
  { key: 'most',       label: 'Most Played',               icon: <FiTrendingUp />,tone: 'warm', kind: 'list',  listFn: (u) => getMostPlayed(u) },
  { key: 'added',      label: 'Recently Added',            icon: <FiPlusCircle />,tone: 'cool', kind: 'list',  listFn: (_u, t) => t /* already newest-first */ },
  { key: 'liked',      label: 'Liked Songs',               icon: <FiHeart />,     tone: 'warm', kind: 'list',  listFn: (_u, t) => t.filter(x => x.isFavorite) },
  { key: 'starred',    label: 'Starred',                   icon: <FiStar />,      tone: 'cool', kind: 'list',  listFn: (_u, t) => t.filter(x => x.rating > 0).sort((a, b) => b.rating - a.rating) },
  { key: 'singer',     label: 'Music By Singer',           icon: <FiMic />,       tone: 'warm', kind: 'group', groupFn: groupByArtist },
  { key: 'language',   label: 'Music By Language',         icon: <FiGlobe />,     tone: 'cool', kind: 'group', groupFn: groupByLanguage },
  { key: 'album',      label: 'Music by Movie / Album',    icon: <FiFilm />,      tone: 'warm', kind: 'group', groupFn: groupByAlbum },
  { key: 'mood',       label: "What's Your Mood",          icon: <FiSmile />,     tone: 'cool', kind: 'group', groupFn: groupByMood },
  { key: 'playlists',  label: 'Your Playlists',            icon: <FiList />,      tone: 'warm', kind: 'playlists' },
  { key: 'wishlist',   label: 'Wish List',                 icon: <FiStar />,      tone: 'cool', kind: 'wishlist' },
  { key: 'update',     label: 'Update Song Info',          icon: <FiEdit2 />,     tone: 'warm', kind: 'list',  listFn: (_u, t) => t, editInfo: true, subtitle: 'Tap the pencil on a song to edit its title, artist or album.' },
  { key: 'delete',     label: 'Delete Songs',              icon: <FiTrash2 />,    tone: 'cool', kind: 'list',  listFn: (_u, t) => t, subtitle: 'Use the trash icon to remove songs from your library.' },
  { key: 'upload',     label: 'Upload Music',              icon: <FiUploadCloud />,tone: 'warm', kind: 'upload' },
];

interface Props {
  tracks: TrackView[];
  userId: number;
  onPlay: (tracks: TrackView[], index: number, title?: string) => void;
  currentTrackId: number | null;
  onChanged: () => void;
  showAds: boolean;
  planTier: string;
  goUpload: () => void;
}

export const MusicDashboard: React.FC<Props> = ({ tracks, userId, onPlay, currentTrackId, onChanged, showAds, planTier, goUpload }) => {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [group, setGroup] = useState<TrackGroup | null>(null);
  const [editTrack, setEditTrack] = useState<TrackView | null>(null);

  const tile = useMemo(() => TILES.find(t => t.key === activeKey) || null, [activeKey]);

  const clickTile = (t: TileDef) => {
    if (t.kind === 'playAll') { if (tracks.length) onPlay(tracks, 0, 'All songs'); return; }
    if (t.kind === 'upload') { goUpload(); return; }
    setGroup(null);
    setActiveKey(t.key);
  };

  const backToHome = () => { setActiveKey(null); setGroup(null); };

  // ---------- Tile grid (home) ----------
  if (!tile) {
    return (
      <div className="md-grid">
        {TILES.map(t => (
          <button key={t.key} className={`md-tile md-${t.tone}`} onClick={() => clickTile(t)}>
            <span className="md-tile-icon">{t.icon}</span>
            <span className="md-tile-label">{t.label}</span>
          </button>
        ))}
      </div>
    );
  }

  // ---------- Playlists ----------
  if (tile.kind === 'playlists') {
    return (
      <MusicPlaylists
        userId={userId}
        allTracks={tracks}
        onBack={backToHome}
        onPlay={onPlay}
        currentTrackId={currentTrackId}
      />
    );
  }

  // ---------- Wishlist ----------
  if (tile.kind === 'wishlist') {
    return <WishlistView userId={userId} onBack={backToHome} />;
  }

  // ---------- Group browse ----------
  if (tile.kind === 'group' && tile.groupFn) {
    if (!group) {
      const groups = tile.groupFn(tracks);
      return (
        <div className="mcv">
          <div className="mcv-head">
            <button className="mcv-back" onClick={backToHome}><FiArrowLeft /></button>
            <div><h3 className="mcv-title">{tile.label}</h3><p className="mcv-subtitle">{groups.length} group{groups.length === 1 ? '' : 's'}</p></div>
          </div>
          <div className="md-groups">
            {groups.map(g => (
              <button key={g.key} className="md-group-card" onClick={() => setGroup(g)}>
                <span className="md-group-label">{g.label}</span>
                <span className="md-group-count">{g.count} song{g.count === 1 ? '' : 's'}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }
    return (
      <MusicCollectionView
        title={group.label}
        subtitle={tile.label}
        tracks={group.tracks}
        userId={userId}
        onBack={() => setGroup(null)}
        onPlay={onPlay}
        currentTrackId={currentTrackId}
        onChanged={onChanged}
        showAds={showAds}
        planTier={planTier}
      />
    );
  }

  // ---------- Flat list ----------
  const listTracks = tile.listFn ? tile.listFn(userId, tracks) : tracks;
  return (
    <>
      <MusicCollectionView
        title={tile.label}
        subtitle={tile.subtitle}
        tracks={listTracks}
        userId={userId}
        onBack={backToHome}
        onPlay={onPlay}
        currentTrackId={currentTrackId}
        onChanged={onChanged}
        showAds={showAds}
        planTier={planTier}
        onEditInfo={tile.editInfo ? setEditTrack : undefined}
      />
      {editTrack && (
        <EditInfoModal
          track={editTrack}
          onClose={() => setEditTrack(null)}
          onSaved={() => { setEditTrack(null); onChanged(); }}
        />
      )}
    </>
  );
};

// =============================================
// Edit Song Info modal
// =============================================
const EditInfoModal: React.FC<{ track: TrackView; onClose: () => void; onSaved: () => void }> = ({ track, onClose, onSaved }) => {
  const [title, setTitle] = useState(track.title);
  const [artist, setArtist] = useState(track.artist);
  const [album, setAlbum] = useState(track.album);
  const [busy, setBusy] = useState(false);
  const [coverFile, setCoverFile] = useState<Blob | null>(null);   // new pick (not yet saved)
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  // Show the existing cover initially.
  useEffect(() => {
    let cancelled = false; let url: string | null = null;
    if (track.coverKey) getAudioUrl(track.coverKey).then(u => { if (cancelled) { if (u) URL.revokeObjectURL(u); return; } url = u; setCoverUrl(u); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [track.coverKey]);

  const pickCover = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (!isValidCover(file)) { window.alert('Pick an image (png/jpg/webp/gif) under 8 MB.'); return; }
    if (coverUrl) URL.revokeObjectURL(coverUrl);
    setCoverFile(file); setCoverUrl(URL.createObjectURL(file));
  };

  const save = async () => {
    setBusy(true);
    let coverKey: string | undefined;
    if (coverFile) { coverKey = await storeAudioBlob(coverFile, undefined, 'cover.jpg'); }
    await updateTrackInfo(track.trackId, { title, artist, album }, coverKey);
    setBusy(false);
    onSaved();
  };

  return (
    <div className="mlg-overlay" role="dialog" aria-modal="true">
      <div className="mlg-modal md-edit">
        <div className="mlg-head"><FiEdit2 className="mlg-head-icon" /><h3>Update Song Info</h3></div>
        <div className="md-edit-cover">
          <button className="md-cover-pick" onClick={() => coverInputRef.current?.click()} title="Set / replace cover art">
            {coverUrl ? <img src={coverUrl} alt="cover" /> : <FiImage />}
          </button>
          <input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickCover} />
          <span className="md-cover-hint">Cover art</span>
        </div>
        <div className="md-edit-fields">
          <label>Title<input value={title} onChange={e => setTitle(e.target.value)} /></label>
          <label>Artist<input value={artist} onChange={e => setArtist(e.target.value)} /></label>
          <label>Album<input value={album} onChange={e => setAlbum(e.target.value)} /></label>
        </div>
        <div className="mlg-actions">
          <button className="mlg-btn mlg-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="mlg-btn mlg-btn-primary" onClick={save} disabled={busy || !title.trim()}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

// =============================================
// Wishlist view
// =============================================
const WishlistView: React.FC<{ userId: number; onBack: () => void }> = ({ userId, onBack }) => {
  const [items, setItems] = useState<WishlistItem[]>(() => getWishlist(userId));
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = () => setItems(getWishlist(userId));

  const add = async () => {
    if (!title.trim()) return;
    setBusy(true);
    await addWishlistItem(userId, { title, artist });
    setTitle(''); setArtist('');
    setBusy(false);
    reload();
  };
  const acquire = async (id: number) => { await setWishlistStatus(id, 'acquired'); reload(); };
  const remove = async (id: number) => { await removeWishlistItem(id); reload(); };

  return (
    <div className="mcv">
      <div className="mcv-head">
        <button className="mcv-back" onClick={onBack}><FiArrowLeft /></button>
        <div><h3 className="mcv-title">Wish List</h3><p className="mcv-subtitle">Music you want to add when you get it</p></div>
      </div>

      <div className="md-wish-add">
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Song or album title" />
        <input value={artist} onChange={e => setArtist(e.target.value)} placeholder="Artist (optional)" />
        <button className="mlg-btn mlg-btn-primary" onClick={add} disabled={busy || !title.trim()}><FiPlusCircle /> Add</button>
      </div>

      {items.length === 0 ? (
        <p className="music-empty">Your wish list is empty.</p>
      ) : (
        <div className="md-wish-list">
          {items.map(w => (
            <div key={w.wishlistId} className={`md-wish-item ${w.status === 'acquired' ? 'got' : ''}`}>
              <div className="md-wish-main">
                <span className="md-wish-title">{w.title}</span>
                {w.artist && <span className="md-wish-artist">{w.artist}</span>}
              </div>
              {w.status === 'acquired' ? <span className="md-wish-badge">Acquired ✓</span> : (
                <button className="md-wish-act" onClick={() => acquire(w.wishlistId)} title="Mark as acquired">Got it</button>
              )}
              <button className="md-wish-del" onClick={() => remove(w.wishlistId)} title="Remove">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MusicDashboard;
